// import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsMutating } from '@tanstack/react-query';

// Services
import {
  getDetailTabsMutationKey, useGetDetailHeader, useGetDetailTabs,
} from '../services/detail/detailQueries';
import { useDetailTabsSaveMutation } from '../services/detail/detailTabsMutation';

// Components
import BannerHeader from '../components/organisms/BannerHeader/BannerHeader';
import DetailTabs from '../components/organisms/DetailTabs/DetailTabs';
import DetailTabsManagementPopover from '../components/organisms/DetailTabsManagementPopover/DetailTabsManagementPopover';
import IconButton from '../components/atoms/IconButton/IconButton';
import BannerContentRow from '../components/molecules/BannerContentRow/BannerContentRow';
import NavigationControl from '../components/molecules/NavigationControl/NavigationControl';
import { BannerTitleMenu } from '../components/molecules/BannerHeaderActionRow/BannerHeaderActionRow';
import CustomVueListContainer from '../components/organisms/CustomVueList/CustomVueList';
import DetailHeaderMenuList from '../components/molecules/DetailHeaderMenuList/DetailHeaderMenuList';

// Utils
import { buildDetailNavUrl, normalizeUrl } from '../utils/url';

// Hooks
import { useContexte } from '../hooks/useContexte';
import useDetailTabs from '../domain/detail/useDetailTabs';
import useDetailTabsManagement from '../domain/detail/useDetailTabsManagement';
import {
  findTabItemByIds,
  getFirstNavigablePath,
  isPathInUsedTabs,
  resolvePathAfterCloseTab,
  resolveRealignmentPath,
} from '../domain/detail/detailUseCase';

/**
 * Persistent detail shell (header + tabs). Stays mounted across tab navigation.
 * @param {object} props
 * @param {ApiPage} [props.page]
 * @param {boolean} props.isTabTransitioning
 * @param {(value: boolean) => void} props.setIsTabTransitioning
 * @returns {JSX.Element}
 */
function DetailShell({ page, isTabTransitioning, setIsTabTransitioning }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isPopoverMenuOpen, setIsPopoverMenuOpen] = useState(false);

  const path = page?.path || '';
  const currentPath = `${location.pathname}${location.search}`;
  const contexte = useContexte();

  const {
    data: detailHeaderData,
    isLoading: isDetailHeaderLoading,
  } = useGetDetailHeader(path, contexte);

  // While tab saves are queued, the optimistic cache is the source of truth: no GET may overwrite it
  // (e.g. new path after closing the active tab). Fetch resumes once the queue is empty.
  const isSavingTabs = useIsMutating({ mutationKey: getDetailTabsMutationKey(contexte) }) > 0;

  const {
    data: detailTabsData,
    isLoading: isDetailTabsLoading,
  } = useGetDetailTabs(path, contexte, {}, { enabled: !isSavingTabs });

  const tabs = detailTabsData?.tabs ?? [];

  const {
    getAllItemsAtLevel,
    getLevelConfigCandidatesAtLevel,
    getTabsAfterToggle,
    setTabFlags,
    startConfigDraft,
    discardConfigDraft,
    getDraftTabs,
  } = useDetailTabsManagement({ tabs });

  const { mutate: saveDetailTabs } = useDetailTabsSaveMutation(path, contexte);

  const {
    tabRows,
    activeTabIds,
    handleTabClickAtLevel,
    getTabHref,
  } = useDetailTabs({
    tabs,
    currentPath,
    searchParams,
    navigate,
  });

  const navigateToPath = useCallback((targetPath) => {
    if (!targetPath) return;
    navigate(buildDetailNavUrl(targetPath, searchParams));
  }, [navigate, searchParams]);

  const handleAddTab = useCallback((tabId, parentIds) => {
    setIsTabTransitioning(true);
    const tabIdPath = [...parentIds, tabId];
    const nextTabs = getTabsAfterToggle(tabIdPath, { used: true });
    const item = findTabItemByIds(nextTabs, tabIdPath);
    const targetPath = item?.path ?? getFirstNavigablePath(item);

    saveDetailTabs(
      { nextTabs, optimistic: false },
      {
        onSuccess: () => {
          navigateToPath(targetPath);
        },
        onSettled: () => {
          setIsTabTransitioning(false);
        },
      },
    );
  }, [getTabsAfterToggle, path, saveDetailTabs, navigateToPath, setIsTabTransitioning]);

  const handleApplyLevel = useCallback(() => {
    const draftTabs = getDraftTabs();
    if (!draftTabs) return;

    const needsRealignment = !isPathInUsedTabs(draftTabs, currentPath);
    const realignmentPath = needsRealignment
      ? resolveRealignmentPath(
        draftTabs,
        [detailHeaderData?.menu?.[0]?.baseUrl ?? detailHeaderData?.previousUrl],
      )
      : null;
    saveDetailTabs(
      { nextTabs: draftTabs, optimistic: false },
      {
        onSuccess: () => {
          discardConfigDraft();
          if (needsRealignment) {
            navigateToPath(realignmentPath);
          }
        },
      },
    );
  }, [
    currentPath,
    discardConfigDraft,
    detailHeaderData,
    getDraftTabs,
    navigateToPath,
    saveDetailTabs,
  ]);

  const handleToggleAvailable = useCallback((parentIds, tabId, available) => {
    setTabFlags([...parentIds, tabId], { available });
  }, [setTabFlags]);

  const renderLevelActions = useCallback((level, parentIds) => (
    <DetailTabsManagementPopover
      level={level}
      candidates={getLevelConfigCandidatesAtLevel(parentIds)}
      allItems={getAllItemsAtLevel(parentIds)}
      onSelectItem={(tabId) => handleAddTab(tabId, parentIds)}
      onToggleAvailable={(tabId, available) => handleToggleAvailable(parentIds, tabId, available)}
      onApply={handleApplyLevel}
      onEnterSettings={startConfigDraft}
      onCancel={discardConfigDraft}
    />
  ), [
    discardConfigDraft,
    getAllItemsAtLevel,
    getLevelConfigCandidatesAtLevel,
    handleAddTab,
    handleApplyLevel,
    handleToggleAvailable,
    startConfigDraft,
  ]);

  const handleCloseTabAtLevel = useCallback((level, tabId) => {
    const parentIds = activeTabIds.slice(0, level);
    const tabIdPath = [...parentIds, tabId];
    const nextTabs = getTabsAfterToggle(tabIdPath, { used: false });

    // Close active tab: navigate first (avoids falling back to the leftmost tab),
    // then optimistic patch + POST in the background.
    if (activeTabIds[level] === tabId) {
      const targetPath = resolvePathAfterCloseTab(nextTabs, tabIdPath)
        ?? resolveRealignmentPath(
          nextTabs,
          [detailHeaderData?.menu?.[0]?.baseUrl ?? detailHeaderData?.previousUrl],
        );
      navigateToPath(targetPath);
    }

    saveDetailTabs({ nextTabs, optimistic: true });
  }, [
    activeTabIds,
    detailHeaderData,
    getTabsAfterToggle,
    navigateToPath,
    saveDetailTabs,
  ]);

  const handlePrevious = () => {
    if (detailHeaderData?.previousUrl) {
      navigate(normalizeUrl(detailHeaderData.previousUrl));
    }
  };

  const handleNext = () => {
    if (detailHeaderData?.nextUrl) {
      navigate(normalizeUrl(detailHeaderData.nextUrl));
    }
  };

  const handleActionMenu = () => {
    setIsPopoverMenuOpen(!isPopoverMenuOpen);
  };

  const handleMenuActionClick = (baseUrl) => {
    if (baseUrl) {
      navigate(normalizeUrl(baseUrl));
      setIsPopoverMenuOpen(false);
    }
  };

  const isOnLastItem = useMemo(() => !detailHeaderData?.nextUrl, [detailHeaderData]);
  const isOnFirstItem = useMemo(() => !detailHeaderData?.previousUrl, [detailHeaderData]);

  const isDetailShellBlocking = (
    (isDetailHeaderLoading && !detailHeaderData)
    || (isDetailTabsLoading && !detailTabsData)
    || isTabTransitioning
  );

  return (
    <>
      <BannerHeader
        title={(
          <BannerTitleMenu
            title={detailHeaderData?.title || 'Detail'}
            action={(
              <IconButton
                icon="chevron-down-range"
                color="no-background-white"
              />
            )}
            open={isPopoverMenuOpen}
            onOpenChange={setIsPopoverMenuOpen}
            onTriggerClick={handleActionMenu}
          >
            <CustomVueListContainer width="480px">
              <DetailHeaderMenuList
                menu={detailHeaderData?.menu ?? []}
                onItemClick={handleMenuActionClick}
              />
            </CustomVueListContainer>
          </BannerTitleMenu>
        )}
        navigationComponent={(
          <NavigationControl
            onPrevious={handlePrevious}
            onNext={handleNext}
            disableNext={isOnLastItem}
            disablePrevious={isOnFirstItem}
          />
        )}
        isLoading={isDetailShellBlocking}
      >
        {detailHeaderData?.fieldsets?.length ? (
          <BannerContentRow fieldsets={detailHeaderData.fieldsets} />
        ) : null}
      </BannerHeader>

      <DetailTabs
        tabRows={tabRows}
        activeTabIds={activeTabIds}
        onTabClick={handleTabClickAtLevel}
        getTabHref={getTabHref}
        onCloseTab={handleCloseTabAtLevel}
        renderLevelActions={renderLevelActions}
        isLoading={isDetailShellBlocking}
      />
    </>
  );
}

export default DetailShell;
